import React from 'react';
import { Line } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import getYear from 'date-fns/getYear';
import getMonth from 'date-fns/getMonth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';

import { theme } from 'theme';
import { getMonthsBetweenDates } from 'shared/functions';
import {
  creditRatingRangeValues,
  creditRatingLongTermRangeValues,
} from 'shared/constants/creditRatingRangeValues';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { scalesTypes } from 'shared/mappings';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    width: 310,
    height: 90,
    zIndex: 0,

    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      height: '90%',
      transform: 'translateY(-50%)',
      borderLeft: ({ notAvailable }) =>
        `2px dashed ${notAvailable ? theme.palette.grey[300] : theme.palette.grey[500]}`,
      zIndex: -1,
    },
    '&:before': {
      left: 'calc(33.3% - 3px)',
    },
    '&:after': {
      right: 'calc(33.3% + 5px)',
    },
  },
  yearItem: {
    width: '33.3%',
    display: 'inline-block',
    textAlign: 'center',
    color: ({ notAvailable }) => (notAvailable ? theme.palette.grey[400] : 'inherit'),
  },
  notAvailable: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const SummaryCardLineChart = ({
  history,
  historyType,
  latestChange,
  ratingScale,
  isESGImpact,
  isESGSentiment,
}) => {
  const sentimentHistory = history.reverse();
  const notAvailable = history.length === 0;
  const classes = useStyles({ notAvailable });
  const currentYear = getYear(new Date());
  const currentMonth = getMonth(new Date()) + 1;
  const labels = getMonthsBetweenDates(+currentYear - 2, +currentYear + 1);

  const getDataBeforeFirstChartYear = () => {
    const data = [];

    history.forEach((item) => {
      const date = getYear(new Date(item.label.toString()));

      if (date < +currentYear - 2) {
        data.push(item);
      }
    });

    return data;
  };

  const drawData = () => {
    const dataBeforeFirstChartYear = getDataBeforeFirstChartYear();
    const currentDate = new Date(`${currentYear}/${currentMonth}`);
    const data = [];
    const pointRadius = [];
    const isWithdrawal = latestChange && latestChange.status === 'withdrawal';
    const withdrawalDate = latestChange && new Date(latestChange.date);
    const shouldStopDrawing = (date) => isWithdrawal && date > withdrawalDate;
    let actualValue =
      dataBeforeFirstChartYear.length !== 0 ? dataBeforeFirstChartYear[0].data : null;

    labels.forEach((value) => {
      const date = new Date(value.toString());
      const searchedData = history.find((item) => item.label === value);

      if (searchedData && value === searchedData.label) {
        pointRadius.push(3);
        actualValue = searchedData.data;
      } else {
        pointRadius.push(0);
      }

      if (!actualValue || date > currentDate || shouldStopDrawing(date)) {
        data.push(null);
      } else {
        data.push(actualValue);
      }
    });

    return { pointRadius, data };
  };

  const selectSuggestedMax = {
    [portfolioTypes.CREDIT_RATING]: {
      [scalesTypes.rs1]: 20,
      [scalesTypes.rs2]: 5,
    },
    default: {
      undefined: 10,
      [scalesTypes.rs3]: 10,
      [scalesTypes.rs4]: 10,
    },
    undefined: {
      undefined: 10,
    },
  };

  const tooltipCallback = {
    [portfolioTypes.CREDIT_RATING]: {
      [scalesTypes.rs1]: (tooltip) =>
        creditRatingRangeValues.find(({ value }) => value === tooltip).label,
      [scalesTypes.rs2]: (tooltip) =>
        creditRatingLongTermRangeValues.find(({ value }) => value === tooltip).label,
    },
    default: {
      undefined: (tooltip) => tooltip,
      [scalesTypes.rs3]: (tooltip) => tooltip,
      [scalesTypes.rs4]: (tooltip) => tooltip,
    },
    undefined: {
      undefined: (tooltip) => tooltip,
    },
  };

  const data = {
    labels:
      (notAvailable && []) ||
      (isESGSentiment ? sentimentHistory.map((item) => item.label) : labels),
    datasets: [
      {
        label: '',
        data:
          (notAvailable && []) ||
          (isESGSentiment ? sentimentHistory.map((item) => item.data) : drawData().data),
        pointRadius: isESGSentiment ? [3, 3, 3] : drawData().pointRadius,
        borderWidth: 4,
        fill: false,
        lineTension: 0.05,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
    ],
  };

  const options = {
    layout: { padding: 5 },
    scales: {
      y: {
        display: false,
        ticks: {
          suggestedMin: 1,
          suggestedMax: selectSuggestedMax[historyType][ratingScale],
        },
        min: isESGImpact ? 0 : 1,
        max: isESGImpact ? 10 : selectSuggestedMax[historyType][ratingScale],
      },
      x: { display: false },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => tooltipCallback[historyType][ratingScale](tooltipItem.raw),
        },
      },
    },
    hover: { mode: null },
  };

  const chartLabels = isESGSentiment
    ? sentimentHistory.map(({ label }) => label)
    : [
        +currentYear.toString().slice(2, 4) - 2,
        +currentYear.toString().slice(2, 4) - 1,
        +currentYear.toString().slice(2, 4),
      ];

  return (
    <Box className={classes.container}>
      <Line data={data} options={options} width={310} height={90} />
      {chartLabels.map((label) => (
        <Typography
          variant="body2"
          color="textSecondary"
          component="span"
          className={classes.yearItem}
          key={label}
        >
          {label}
        </Typography>
      ))}
      {notAvailable && (
        <Typography variant="subtitle2" component="p" className={classes.notAvailable}>
          NA
        </Typography>
      )}
    </Box>
  );
};

SummaryCardLineChart.defaultProps = {
  history: [],
  historyType: 'default',
  latestChange: undefined,
  ratingScale: undefined,
  isESGImpact: false,
  isESGSentiment: false,
};

SummaryCardLineChart.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  historyType: PropTypes.oneOf([...Object.values(portfolioTypes), 'default']),
  latestChange: PropTypes.shape({
    status: PropTypes.string,
    date: PropTypes.string,
  }),
  ratingScale: PropTypes.oneOf([...Object.values(scalesTypes)]),
  isESGImpact: PropTypes.bool,
  isESGSentiment: PropTypes.bool,
};

export default SummaryCardLineChart;
